/**
 * API Client for Backend Communication
 * =====================================
 *
 * Handles HTTP communication with the FastAPI backend.
 * Automatically attaches JWT tokens from Supabase auth.
 */

import { supabase } from './supabase'
import { telemetry } from './telemetry'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/** WebSocket base URL for progress (http -> ws, https -> wss). */
export function getWebSocketBaseUrl(): string {
  const base = API_BASE_URL
  if (base.startsWith('https://')) return base.replace('https://', 'wss://')
  if (base.startsWith('http://')) return base.replace('http://', 'ws://')
  return `wss://${base}`
}

/**
 * URL del proxy de imagen en la API. Usar para img src y descargas desde el
 * frontend desplegado (evita CORS/visibilidad del bucket Supabase Storage).
 */
export function getImageAssetUrl(imageId: string): string {
  return `${API_BASE_URL}/api/visual/asset/${imageId}`
}

/**
 * API Error class for structured error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: Record<string, unknown>,
    public path?: string,
    public method: string = 'GET',
    public requestId?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Get the current Supabase JWT token
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

/**
 * Generic fetch wrapper with auth and error handling
 */
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  const url = `${API_BASE_URL}${endpoint}`
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: headers as HeadersInit,
    })
    
    // Handle non-2xx responses
    if (!response.ok) {
      let errorData: Record<string, unknown> = {}
      try {
        errorData = (await response.json()) as Record<string, unknown>
      } catch {
        errorData = { detail: response.statusText }
      }

      const requestId =
        typeof errorData?.request_id === 'string'
          ? errorData.request_id
          : undefined
      const errorCode =
        typeof errorData?.code === 'string' ? errorData.code : undefined
      const method = (options.method as string) || 'GET'

      const err = new ApiError(
        (errorData.detail as string) ||
          (errorData.message as string) ||
          'Request failed',
        response.status,
        errorData,
        endpoint,
        method,
        requestId
      )
      telemetry.reportApiError({
        path: endpoint,
        method,
        status: response.status,
        request_id: requestId,
        error_code: errorCode,
      })
      throw err
    }
    
    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T
    }
    
    return await response.json()
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    // Network or other errors
    const method = (options.method as string) || 'GET'
    const err = new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0,
      error instanceof Error ? { cause: error.message } : undefined,
      endpoint,
      method
    )
    telemetry.reportApiError({
      path: endpoint,
      method,
      status: 0,
    })
    throw err
  }
}

// ============================================
// AI Configuration Types
// ============================================

export interface AgentConfig {
  agent_key: string
  display_name: string
  team: string
  description: string
  system_prompt: string | null
  model_override: string | null
  temperature: number
  max_tokens: number
  max_retries: number
  timeout_seconds: number
  is_active: boolean
  updated_at: string | null
}

export interface AgentConfigUpdate {
  system_prompt?: string | null
  model_override?: string | null
  temperature?: number | null
  max_tokens?: number | null
  max_retries?: number | null
  timeout_seconds?: number | null
  is_active?: boolean
  task_type?: string | null
  thinking_level?: string | null
  top_p?: number | null
}

export interface PipelineConfig {
  pipeline_key: string
  display_name: string
  config: Record<string, unknown>
  updated_at: string | null
}

export interface ModelInfo {
  name: string
  provider: string
  model_id: string
  max_tokens: number
  temperature: number
  cost_per_1k_input: number
  cost_per_1k_output: number
  supports_vision: boolean
  supports_function_calling: boolean
  context_window: number
}

// ============================================
// AI Configuration API Methods
// ============================================

export async function getAgentConfigs(): Promise<AgentConfigListItem[]> {
  return fetchAPI<AgentConfigListItem[]>('/api/config/agents')
}

export async function updateAgentConfig(agentKey: string, data: AgentConfigUpdate): Promise<AgentConfigListItem>
export async function updateAgentConfig(agentId: string, orgId: string, data: AgentConfigUpdate): Promise<AgentConfigListItem>
export async function updateAgentConfig(
  agentKeyOrId: string,
  dataOrOrgId: AgentConfigUpdate | string,
  data?: AgentConfigUpdate
): Promise<AgentConfigListItem> {
  if (data !== undefined && typeof dataOrOrgId === 'string') {
    return fetchAPI<AgentConfigListItem>(
      `/api/config/agents/${encodeURIComponent(agentKeyOrId)}?organization_id=${encodeURIComponent(dataOrOrgId)}`,
      { method: 'PUT', body: JSON.stringify(data) }
    )
  }
  return fetchAPI<AgentConfigListItem>(`/api/config/agents/${agentKeyOrId}`, {
    method: 'PUT',
    body: JSON.stringify(dataOrOrgId as AgentConfigUpdate),
  })
}

export async function resetAgentConfig(agentKey: string): Promise<AgentConfigListItem> {
  return fetchAPI<AgentConfigListItem>(`/api/config/agents/${agentKey}/reset`, { method: 'POST' })
}

export async function resetAllAgentConfigs(): Promise<void> {
  return fetchAPI<void>('/api/config/agents/reset', { method: 'POST' })
}

export async function getPipelineConfigs(): Promise<PipelineConfig[]> {
  return fetchAPI<PipelineConfig[]>('/api/config/pipelines')
}

export async function updatePipelineConfig(
  pipelineKey: string,
  data: { config: Record<string, unknown> }
): Promise<PipelineConfig> {
  return fetchAPI<PipelineConfig>(`/api/config/pipelines/${pipelineKey}`, { method: 'PUT', body: JSON.stringify(data) })
}

export async function resetAllPipelineConfigs(): Promise<void> {
  return fetchAPI<void>('/api/config/pipelines/reset', { method: 'POST' })
}

export async function getAvailableModels(): Promise<ModelInfo[]> {
  const response = await fetchAPI<{ text_models: ModelInfo[]; image_models: ModelInfo[] }>('/api/config/models')
  return response.text_models ?? []
}

export async function getConfigDefaults(): Promise<{ agents: Record<string, Record<string, unknown>>; pipelines: Record<string, Record<string, unknown>> }> {
  return fetchAPI<{ agents: Record<string, Record<string, unknown>>; pipelines: Record<string, Record<string, unknown>> }>('/api/config/defaults')
}

// ============================================
// Prompt history (GET history, POST restore)
// ============================================

/** Single prompt version from GET /api/config/agents/{agent_key}/prompts/history */
export interface PromptHistoryItem {
  id: string
  version: number
  system_prompt: string
  is_active: boolean
  created_at: string
}

/**
 * List prompt versions for an agent (org + agent_type), ordered by version desc.
 * GET /api/config/agents/{agent_key}/prompts/history?organization_id=
 */
export async function getPromptHistory(
  agentKey: string,
  organizationId?: string
): Promise<PromptHistoryItem[]> {
  const params = new URLSearchParams()
  if (organizationId) params.set('organization_id', organizationId)
  const qs = params.toString()
  const url = `/api/config/agents/${encodeURIComponent(agentKey)}/prompts/history${qs ? `?${qs}` : ''}`
  return fetchAPI<PromptHistoryItem[]>(url)
}

/**
 * Set the given prompt version as active and deactivate others.
 * Returns the updated agent config (same shape as GET agent).
 * POST /api/config/agents/{agent_key}/prompts/{prompt_id}/restore?organization_id=
 */
export async function restorePromptVersion(
  agentKey: string,
  promptId: string,
  organizationId?: string
): Promise<AgentConfigResponse> {
  const params = new URLSearchParams()
  if (organizationId) params.set('organization_id', organizationId)
  const qs = params.toString()
  const url = `/api/config/agents/${encodeURIComponent(agentKey)}/prompts/${encodeURIComponent(promptId)}/restore${qs ? `?${qs}` : ''}`
  return fetchAPI<AgentConfigResponse>(url, { method: 'POST' })
}

// ============================================
// Admin Config API (organization-scoped, Bearer token via fetchAPI)
// ============================================

/** Default block returned by API (from core / AGENT_DEFAULTS + prompts). */
export interface AgentConfigDefaultBlock {
  task_type?: string | null
  model_override?: string | null
  temperature?: number | null
  max_tokens?: number | null
  max_retries?: number | null
  timeout_seconds?: number | null
  thinking_level?: string | null
  top_p?: number | null
  system_prompt?: string | null
}

/** Override block from agent_config + prompts (only present keys). */
export interface AgentConfigOverrideBlock {
  task_type?: string | null
  model_override?: string | null
  temperature?: number | null
  max_tokens?: number | null
  max_retries?: number | null
  timeout_seconds?: number | null
  thinking_level?: string | null
  top_p?: number | null
  system_prompt?: string | null
}

/** Response item from GET /api/config/agents (default + override). */
export interface AgentConfigListItem {
  agent_id: string
  display_name: string
  team: string
  default: AgentConfigDefaultBlock
  override: AgentConfigOverrideBlock | null
}

/** Single-agent response from GET /api/config/agents/{id} and POST restore. Same shape as list item. */
export type AgentConfigResponse = AgentConfigListItem

/** Effective (merged) config: override ?? default per field. Use for form state and display. */
export interface AgentConfigEffective {
  agent_key: string
  display_name: string
  team: string
  description?: string | null
  system_prompt: string | null
  model_override: string | null
  temperature: number
  max_tokens: number
  max_retries: number
  timeout_seconds: number
  thinking_level?: string | null
  top_p?: number | null
  is_active: boolean
  updated_at?: string | null
}

/**
 * Derive effective config from API list item (override wins over default).
 * Use this to fill forms and display current values.
 */
export function getEffectiveAgentConfig(item: AgentConfigListItem): AgentConfigEffective {
  const d = item.default
  const o = item.override
  return {
    agent_key: item.agent_id,
    display_name: item.display_name,
    team: item.team,
    description: undefined,
    system_prompt: (o?.system_prompt ?? d?.system_prompt) ?? null,
    model_override: (o?.model_override ?? d?.model_override) ?? null,
    temperature: (o?.temperature ?? d?.temperature) ?? 0.6,
    max_tokens: (o?.max_tokens ?? d?.max_tokens) ?? 4096,
    max_retries: (o?.max_retries ?? d?.max_retries) ?? 3,
    timeout_seconds: (o?.timeout_seconds ?? d?.timeout_seconds) ?? 120,
    thinking_level: o?.thinking_level ?? d?.thinking_level ?? null,
    top_p: o?.top_p ?? d?.top_p ?? null,
    is_active: true,
    updated_at: null,
  }
}

/** Response from GET /api/config/models (backend may return id + fields). */
export interface ModelRegistryItem {
  id?: string
  name?: string
  provider?: string
  model_id?: string
  model_type?: string
  supports_thinking_level?: boolean
  supports_vision?: boolean
  context_window?: number
  max_tokens?: number
  [key: string]: unknown
}

/** Body for PUT /api/config/pipelines (partial keys per pipeline). */
export interface PipelineConfigsUpdatePayload {
  orchestrator?: Record<string, unknown>
  content?: Record<string, unknown>
  visual?: Record<string, unknown>
  strategy?: Record<string, unknown>
}

export function fetchAgentConfigs(orgId: string): Promise<AgentConfigListItem[]> {
  return fetchAPI<AgentConfigListItem[]>(`/api/config/agents?organization_id=${encodeURIComponent(orgId)}`)
}

export function fetchModels(): Promise<ModelRegistryItem[] | ModelInfo[]> {
  return fetchAPI<ModelRegistryItem[] | ModelInfo[]>('/api/config/models')
}

export function fetchPipelineConfigs(orgId: string): Promise<PipelineConfig[]> {
  return fetchAPI<PipelineConfig[]>(`/api/config/pipelines?organization_id=${encodeURIComponent(orgId)}`)
}

export function updatePipelineConfigs(
  orgId: string,
  data: PipelineConfigsUpdatePayload
): Promise<PipelineConfig[] | Record<string, PipelineConfig>> {
  const keys = ['orchestrator', 'content', 'visual', 'strategy'] as const
  const payloads = keys.filter((k) => data[k] != null)
  if (payloads.length === 0) {
    return fetchAPI<PipelineConfig[] | Record<string, PipelineConfig>>(
      `/api/config/pipelines?organization_id=${encodeURIComponent(orgId)}`,
      { method: 'PUT', body: JSON.stringify(data) }
    )
  }
  return Promise.all(
    payloads.map((key) =>
      fetchAPI<PipelineConfig>(
        `/api/config/pipelines/${key}?organization_id=${encodeURIComponent(orgId)}`,
        { method: 'PUT', body: JSON.stringify({ config: data[key] }) }
      )
    )
  ).then((results) => (results.length === 1 ? results[0] : Object.fromEntries(payloads.map((k, i) => [k, results[i]]))) as PipelineConfig[] | Record<string, PipelineConfig>)
}

export function fetchVisualConfig(orgId: string): Promise<Record<string, unknown>> {
  return fetchAPI<Record<string, unknown>>(`/api/config/visual?organization_id=${encodeURIComponent(orgId)}`)
}

export function updateVisualConfig(
  orgId: string,
  data: Record<string, unknown>
): Promise<Record<string, unknown>> {
  return fetchAPI<Record<string, unknown>>(
    `/api/config/visual?organization_id=${encodeURIComponent(orgId)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  )
}

export interface BrandConfig {
  name: string
  slug: string
  voice_tone: string
  content_pillars: string[]
  visual_palette: { primary: string; accent: string }
  channels: string[]
  guidelines: string
}

export function fetchBrandConfig(orgId: string): Promise<BrandConfig> {
  return fetchAPI<BrandConfig>(`/api/config/brand?organization_id=${encodeURIComponent(orgId)}`)
}

export function updateBrandConfig(
  orgId: string,
  data: Partial<BrandConfig>
): Promise<BrandConfig> {
  return fetchAPI<BrandConfig>(
    `/api/config/brand?organization_id=${encodeURIComponent(orgId)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  )
}

// ============================================
// Organization API Keys
// ============================================

export interface OrgApiKeyStatus {
  provider: string
  key_preview: string
  updated_at: string
}

export async function getOrgApiKeys(): Promise<OrgApiKeyStatus[]> {
  return fetchAPI<OrgApiKeyStatus[]>('/api/config/api-keys')
}

export async function setOrgApiKey(provider: string, key: string): Promise<void> {
  return fetchAPI<void>(`/api/config/api-keys/${encodeURIComponent(provider)}`, {
    method: 'PUT',
    body: JSON.stringify({ key }),
  })
}

export async function deleteOrgApiKey(provider: string): Promise<void> {
  await fetchAPI<unknown>(`/api/config/api-keys/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  })
}

// ============================================
// Organizations & Invitations
// ============================================

export interface OrganizationCreateRequest {
  name: string
  industry?: string | null
  website?: string | null
}

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'paused'

export interface Organization {
  id: string
  name: string
  industry: string | null
  website: string | null
  plan: string
  plan_id: string
  subscription_status: SubscriptionStatus
  current_period_end: string | null
  stripe_customer_id: string | null
  monthly_budget_limit: number | null
  created_at: string
  logo_url: string | null
  ui_primary_color: string | null
  ui_accent_color: string | null
}

export interface OrgBrandingUpdateRequest {
  logo_url?: string | null
  ui_primary_color?: string
  ui_accent_color?: string
}

export interface OrgMember {
  id: string
  full_name: string | null
  email: string | null
  role: string
  created_at: string
}

export interface Invitation {
  id: string
  organization_id: string
  email: string
  role: string
  token: string
  invited_by: string | null
  accepted_at: string | null
  expires_at: string
  created_at: string
}

export interface InvitationPublic {
  email: string
  role: string
  organization_name: string
  expires_at: string
}

export interface InvitationCreateRequest {
  email: string
  role?: string
}

export async function createOrganization(
  data: OrganizationCreateRequest
): Promise<Organization> {
  return fetchAPI<Organization>('/api/organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export interface BrandCalibrationPayload {
  step1: { company_name: string; industry: string; tagline: string; value_proposition: string }
  step2: { tone_adjectives: string[]; positive_phrases: string[]; negative_phrases: string[] }
  step3: { proof_points: Array<{ claim: string; client: string; year: number | null; source: string; verified: boolean }> }
  step4: { calibration_samples: string[] }
  primary_channel: string
  primary_persona_id: string | null
}

export interface BrandCalibrationResponse {
  brand_config_id: string
  calibration_task_id?: string
}

export async function submitBrandCalibration(
  payload: BrandCalibrationPayload
): Promise<BrandCalibrationResponse> {
  return fetchAPI<BrandCalibrationResponse>('/api/organizations/brand-calibration', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getOrganizationMembers(): Promise<OrgMember[]> {
  return fetchAPI<OrgMember[]>('/api/organizations/members')
}

export async function createInvitation(
  data: InvitationCreateRequest
): Promise<Invitation> {
  return fetchAPI<Invitation>('/api/organizations/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getOrganizationInvitations(): Promise<Invitation[]> {
  return fetchAPI<Invitation[]>('/api/organizations/invitations')
}

export async function getInvitationByToken(
  token: string
): Promise<InvitationPublic> {
  return fetchAPI<InvitationPublic>(`/api/invitations/${token}`)
}

export async function acceptInvitation(token: string): Promise<{ message: string }> {
  return fetchAPI<{ message: string }>(`/api/invitations/${token}/accept`, {
    method: 'POST',
  })
}

export async function getOrganization(): Promise<Organization> {
  return fetchAPI<Organization>('/api/organizations/me')
}

export async function updateOrgBranding(
  data: OrgBrandingUpdateRequest
): Promise<Organization> {
  return fetchAPI<Organization>('/api/organizations/me/branding', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function createCheckoutSession(lookupKey: string): Promise<{ url: string }> {
  return fetchAPI<{ url: string }>('/api/billing/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ lookup_key: lookupKey }),
  })
}

export async function createBillingPortalSession(): Promise<{ url: string }> {
  return fetchAPI<{ url: string }>('/api/billing/create-portal-session', {
    method: 'POST',
  })
}

// ============================================
// Content Task Types
// ============================================

export interface ContentTaskCreateRequest {
  persona_id: string
  content_type: string
  channel: string
  topic: string
  brief?: string
  keywords?: string[]
  word_count_target?: number
  include_research?: boolean
  research_query?: string
  conversation_id?: string
}

export interface ContentTaskCreateResponse {
  task_id: string
  estado: string
  mensaje: string
  celery_task_id?: string
}

export interface CancelTaskResponse {
  mensaje: string
}

// GET /api/content/tareas/{tarea_id} response (tarea + piezas with resolved sources)
export interface ContentTaskDetailTarea {
  id: string
  organization_id: string
  estado: string
  tipo_contenido: string
  canal: string
  persona_id: string
  tema: string
  created_at: string
  started_at: string | null
  completed_at: string | null
  error_message: string | null
  conversation_id?: string | null
}

export interface ContentTaskDetailPiece {
  id: string
  task_id: string
  persona_id: string | null
  title: string | null
  body: string
  excerpt: string | null
  cta: string | null
  status: string
  current_version: number | null
  word_count: number | null
  created_at: string
  updated_at?: string
  sources?: Array<{ id: number; title?: string; url: string; domain?: string; snippet?: string }>
  research_summary?: string
  research_key_findings?: string[]
}

export interface ContentTaskDetailResponse {
  tarea: ContentTaskDetailTarea
  piezas_contenido: ContentTaskDetailPiece[]
  react_flow?: unknown
  costos?: unknown
}

// ============================================
// Content Task API Methods
// ============================================

/**
 * Create a new content generation task
 * 
 * Maps frontend field names (English) to backend field names (Spanish)
 * as expected by the TareaContenidoCreate Pydantic model.
 */
export async function createContentTask(
  data: ContentTaskCreateRequest
): Promise<ContentTaskCreateResponse> {
  const payload: Record<string, unknown> = {
    tipo_contenido: data.content_type,
    canal: data.channel,
    persona_id: data.persona_id,
    tema: data.topic,
    brief: data.brief || '',
    palabras_clave: data.keywords || [],
    palabras_objetivo: data.word_count_target,
    incluir_research: data.include_research ?? true,
    query_research: data.research_query,
    max_iteraciones: 3,
    umbral_calidad: 7.0,
    generar_visual: true,
  }
  if (data.conversation_id != null) {
    payload.conversation_id = data.conversation_id
  }

  return fetchAPI<ContentTaskCreateResponse>('/api/content/tareas', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/**
 * Cancel a pending or running content task
 */
export async function cancelContentTask(taskId: string): Promise<CancelTaskResponse> {
  return fetchAPI<CancelTaskResponse>(
    `/api/content/tareas/${taskId}/cancelar`,
    {
      method: 'POST',
    }
  )
}

/**
 * Re-execute a failed or cancelled task
 */
export async function rerunContentTask(
  taskId: string
): Promise<ContentTaskCreateResponse> {
  return fetchAPI<ContentTaskCreateResponse>(
    `/api/content/tareas/${taskId}/reejecutar`,
    { method: 'POST' }
  )
}

/**
 * Get a single content task with pieces and resolved sources (from research_cache).
 * Use this for the task detail view so pieces include sources when the task used research.
 */
export async function getContentTask(
  taskId: string
): Promise<ContentTaskDetailResponse> {
  return fetchAPI<ContentTaskDetailResponse>(
    `/api/content/tareas/${taskId}`
  )
}

/**
 * Save a versioned edit for a content piece.
 * PUT /api/content/contenido/{content_id}/versioned
 * Returns the updated piece with incremented current_version.
 */
export async function saveContentVersion(
  contentId: string,
  payload: { body: string; title?: string; excerpt?: string; cta?: string; edited_with_ai?: boolean }
): Promise<ContentTaskDetailPiece> {
  const response = await fetchAPI<ContentTaskDetailPiece>(
    `/api/content/contenido/${contentId}/versioned`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  )
  return {
    id: response.id,
    task_id: response.task_id,
    persona_id: response.persona_id ?? null,
    title: response.title ?? null,
    body: response.body,
    excerpt: response.excerpt ?? null,
    cta: response.cta ?? null,
    status: response.status ?? 'draft',
    current_version: response.current_version ?? null,
    word_count: response.word_count ?? null,
    created_at: response.created_at,
    updated_at: response.updated_at,
    sources: response.sources,
    research_summary: response.research_summary,
    research_key_findings: response.research_key_findings,
  }
}

/** Response from POST /api/content/contenido/:id/edit-with-ai */
export interface EditWithAIResponse {
  edited_text: string
  full_body: string
}

/**
 * Edit a content piece with AI (instruction, optional selection).
 * POST /api/content/contenido/{content_id}/edit-with-ai
 */
export async function editContentWithAI(
  contentId: string,
  instruction: string,
  options?: { selection?: string; context?: 'full' | 'selection_only' }
): Promise<EditWithAIResponse> {
  return fetchAPI<EditWithAIResponse>(
    `/api/content/contenido/${contentId}/edit-with-ai`,
    {
      method: 'POST',
      body: JSON.stringify({
        instruction,
        selection: options?.selection ?? null,
        context: options?.context ?? (options?.selection ? 'selection_only' : 'full'),
      }),
    }
  )
}

// ============================================
// Chat / Task draft from conversation
// ============================================

export interface ConversationListItem {
  id: string
  organization_id: string
  user_id: string
  title: string | null
  created_at: string
  updated_at: string
  /** When API returns it: enabled sources/tools for this conversation. */
  active_sources?: string[]
}

/** Single conversation detail (GET /api/chat/conversations/:id). */
export interface ConversationDetail {
  id: string
  organization_id: string
  user_id: string
  title: string | null
  created_at: string
  updated_at: string
  active_sources?: string[]
}

/** Single message (GET/POST /api/chat/conversations/:id/messages). */
export interface ChatMessage {
  id: string
  conversation_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  created_at: string
}

/** Payload for PATCH /api/chat/conversations/:id. */
export interface ConversationUpdatePayload {
  title?: string
  active_sources?: string[]
}

/** Draft returned by API from a conversation (matches core ConversationTaskDraft). */
export interface TaskDraftResponse {
  content_type: string
  channel: string
  topic: string
  brief: string
  keywords?: string[]
  word_count_target?: number | null
  include_research?: boolean
  research_query?: string | null
  persona_hint?: string | null
  /** Optional: when client has resolved persona, e.g. for tests or pre-filled flows. */
  persona_id?: string | null
}

/**
 * List conversations for the current user (for "Create task from conversation").
 */
export async function listConversations(
  limit: number = 20,
  offset: number = 0
): Promise<ConversationListItem[]> {
  const params = new URLSearchParams()
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  return fetchAPI<ConversationListItem[]>(
    `/api/chat/conversations?${params.toString()}`
  )
}

/**
 * Get a single conversation by id (for active_sources and detail view).
 */
export async function getConversation(
  conversationId: string
): Promise<ConversationDetail> {
  return fetchAPI<ConversationDetail>(
    `/api/chat/conversations/${conversationId}`
  )
}

/**
 * Update a conversation (title and/or active_sources).
 * Backend may ignore active_sources until the column exists.
 */
export async function updateConversation(
  conversationId: string,
  payload: ConversationUpdatePayload
): Promise<ConversationDetail> {
  return fetchAPI<ConversationDetail>(
    `/api/chat/conversations/${conversationId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  )
}

/**
 * Generate a content task draft from a conversation (conversation -> structured draft).
 * Backend uses core extractor to produce draft; user confirms/edits in UI then creates task.
 */
export async function getTaskDraft(
  conversationId: string
): Promise<TaskDraftResponse> {
  const endpoint = `/api/content/generate-task-draft?conversation_id=${encodeURIComponent(conversationId)}`
  return fetchAPI<TaskDraftResponse>(endpoint, { method: 'GET' })
}

/**
 * Create a new conversation. Title is optional.
 */
export async function createConversation(
  title?: string | null
): Promise<ConversationDetail> {
  return fetchAPI<ConversationDetail>('/api/chat/conversations', {
    method: 'POST',
    body: JSON.stringify({ title: title ?? null }),
  })
}

/**
 * Delete a conversation and its messages.
 */
export async function deleteConversation(
  conversationId: string
): Promise<void> {
  await fetchAPI<unknown>(
    `/api/chat/conversations/${conversationId}`,
    { method: 'DELETE' }
  )
}

/**
 * List messages for a conversation, ordered by created_at ascending.
 */
export async function listMessages(
  conversationId: string,
  limit: number = 50,
  offset: number = 0
): Promise<ChatMessage[]> {
  const params = new URLSearchParams()
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  return fetchAPI<ChatMessage[]>(
    `/api/chat/conversations/${conversationId}/messages?${params.toString()}`
  )
}

/**
 * Send a message to a conversation (user, assistant, or system).
 */
export async function sendMessage(
  conversationId: string,
  role: 'user' | 'assistant' | 'system',
  content: string
): Promise<ChatMessage> {
  return fetchAPI<ChatMessage>(
    `/api/chat/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ role, content }),
    }
  )
}

/**
 * Ask the backend to generate an AI assistant reply for the conversation.
 * The backend reads the conversation history, calls the LLM, stores and returns the reply.
 */
export async function generateChatReply(conversationId: string): Promise<ChatMessage> {
  return fetchAPI<ChatMessage>(
    `/api/chat/conversations/${conversationId}/reply`,
    { method: 'POST' }
  )
}

// ============================================
// Visual Task Types
// ============================================

export interface VisualTask {
  id: string
  organization_id: string
  content_id: string | null
  /** Content task ID (content_tasks.id) to link to /content/:taskId */
  content_task_id?: string | null
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  template_id: string | null
  channel: string
  prompt_data: Record<string, any>
  created_at: string
  completed_at: string | null
}

export interface GeneratedImage {
  id: string
  visual_task_id: string
  storage_path: string
  public_url: string
  width: number | null
  height: number | null
  model_used: string
  prompt_used: string
  created_at: string
  /** When set, this image was produced by editing the given image (lineage). */
  source_image_id?: string | null
  /** Optional text returned alongside the image by the model (commentary, alt-text, etc.). */
  accompanying_text?: string | null
}

export interface VisualTaskCreateRequest {
  content_id?: string | null
  content_excerpt: string
  template_id?: string | null
  canal?: string
  max_refinements?: number
  /** Number of images to generate (1-4). */
  num_images?: number
  /** Style preset: neural_cartography | professional | creative | minimal | etc. */
  preset?: string
  /** Model identifier (e.g. default, gemini3, imagen-4.0-generate-001). */
  model_used?: string
  /** Override aspect ratio (e.g. 1:1, 16:9, 9:16). */
  aspect_ratio?: string
  /** Override image size (1K, 2K, 4K). */
  image_size?: string
  /** Override thinking level (low, medium, high). */
  thinking_level?: string
  /** When set, use this image as reference for a new generation (edit-from-image flow). */
  source_image_id?: string | null
  /** Negative prompt: elements to exclude from the generated image. */
  negative_prompt?: string
  /** When true, the model searches Google for grounding context before generating. */
  use_grounding?: boolean
}

/** Payload sent to POST /api/visual/generar */
interface VisualTaskCreatePayload {
  content_id: string | null
  content_excerpt: string
  template_id: string | null
  canal: string
  max_refinements: number
  num_images?: number
  style_preset?: string
  model_used?: string
  aspect_ratio?: string
  image_size?: string
  thinking_level?: string
  source_image_id?: string | null
  negative_prompt?: string
  use_grounding?: boolean
}

export interface VisualTaskCreateResponse {
  task_id: string
  estado: string
  mensaje: string
  celery_task_id?: string
}

// ============================================
// Visual Task API Methods
// ============================================

/**
 * Create a new visual generation task
 */
export async function createVisualTask(
  data: VisualTaskCreateRequest
): Promise<VisualTaskCreateResponse> {
  const payload: VisualTaskCreatePayload = {
    content_id: data.content_id || null,
    content_excerpt: data.content_excerpt,
    template_id: data.template_id || null,
    canal: data.canal || 'social',
    max_refinements: data.max_refinements || 2,
  }
  if (data.num_images != null) payload.num_images = data.num_images
  if (data.preset != null) payload.style_preset = data.preset
  if (data.model_used != null) payload.model_used = data.model_used
  if (data.aspect_ratio != null) payload.aspect_ratio = data.aspect_ratio
  if (data.image_size != null) payload.image_size = data.image_size
  if (data.thinking_level != null) payload.thinking_level = data.thinking_level
  if (data.source_image_id != null) payload.source_image_id = data.source_image_id
  if (data.negative_prompt) payload.negative_prompt = data.negative_prompt
  if (data.use_grounding != null) payload.use_grounding = data.use_grounding

  return fetchAPI<VisualTaskCreateResponse>('/api/visual/generar', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/**
 * List visual tasks for an organization (paginated)
 */
export async function listVisualTasks(
  filters?: { status?: string; limit?: number; offset?: number }
): Promise<VisualTask[]> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('estado', filters.status)
  if (filters?.limit != null) params.set('limite', String(filters.limit))
  if (filters?.offset != null && filters.offset > 0) params.set('offset', String(filters.offset))
  const qs = params.toString()
  return fetchAPI<VisualTask[]>(`/api/visual/tareas${qs ? `?${qs}` : ''}`)
}

/**
 * Get a single visual task with images
 */
export async function getVisualTask(taskId: string): Promise<{
  tarea: VisualTask
  imagenes: GeneratedImage[]
}> {
  return fetchAPI(`/api/visual/tareas/${taskId}`)
}

/**
 * Get images for a visual task
 */
export async function getTaskImages(taskId: string): Promise<GeneratedImage[]> {
  return fetchAPI<GeneratedImage[]>(`/api/visual/tareas/${taskId}/imagenes`)
}

/**
 * Get all images for a content piece
 */
export async function getContentImages(contentId: string): Promise<GeneratedImage[]> {
  return fetchAPI<GeneratedImage[]>(`/api/visual/content/${contentId}/imagenes`)
}

/**
 * Cancel a pending or running visual task
 */
export async function cancelVisualTask(taskId: string): Promise<{ mensaje: string }> {
  return fetchAPI<{ mensaje: string }>(
    `/api/visual/tareas/${taskId}/cancelar`,
    {
      method: 'POST',
    }
  )
}

export interface EditVisualImageRequest {
  source_image_id: string
  instruction: string
  content_id?: string | null
  visual_task_id?: string | null
  /** Optional mask image as PNG data URL (base64). When provided, only the masked area is edited. */
  mask_data_url?: string | null
}

export interface EditVisualImageResponse {
  image_id?: string
  mensaje?: string
  [key: string]: unknown
}

export interface UploadReferenceImageResponse {
  image_id: string
  public_url: string
}

/**
 * Upload a reference image for image-to-image generation.
 * POST /api/visual/upload-reference (multipart/form-data)
 */
export async function uploadReferenceImage(
  file: File
): Promise<UploadReferenceImageResponse> {
  const token = await getAuthToken()

  const formData = new FormData()
  formData.append('file', file)

  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const url = `${API_BASE_URL}/api/visual/upload-reference`

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!response.ok) {
    let errorData: Record<string, unknown> = {}
    try {
      errorData = (await response.json()) as Record<string, unknown>
    } catch {
      errorData = { detail: response.statusText }
    }
    throw new ApiError(
      (errorData.detail as string) || (errorData.message as string) || 'Upload failed',
      response.status,
      errorData,
      '/api/visual/upload-reference',
      'POST'
    )
  }

  return response.json() as Promise<UploadReferenceImageResponse>
}

/**
 * Edit/annotate an existing image with an instruction.
 */
export async function editVisualImage(
  request: EditVisualImageRequest
): Promise<EditVisualImageResponse> {
  return fetchAPI<EditVisualImageResponse>('/api/visual/editar-imagen', {
    method: 'POST',
    body: JSON.stringify({
      source_image_id: request.source_image_id,
      edit_instruction: request.instruction,
      content_id: request.content_id ?? null,
      visual_task_id: request.visual_task_id ?? null,
      ...(request.mask_data_url != null && request.mask_data_url !== ''
        ? { mask_data_url: request.mask_data_url }
        : {}),
    }),
  })
}

export interface OutpaintImageRequest {
  direction: 'left' | 'right' | 'top' | 'bottom' | 'all'
  expand_px: number
  prompt?: string
}

export interface OutpaintImageResponse {
  task_id: string
  message: string
}

/**
 * Expand the canvas of an image in a given direction.
 * POST /api/visual/imagenes/{image_id}/outpaint
 */
export async function outpaintImage(
  imageId: string,
  request: OutpaintImageRequest
): Promise<OutpaintImageResponse> {
  return fetchAPI<OutpaintImageResponse>(
    `/api/visual/imagenes/${encodeURIComponent(imageId)}/outpaint`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  )
}

export interface RemoveBackgroundResponse {
  new_image_id: string
}

/**
 * Remove background from a generated image.
 * POST /api/visual/imagenes/{image_id}/remove-background
 * Returns the id of the newly created image (source_image_id points to the original).
 */
export async function removeImageBackground(
  imageId: string
): Promise<RemoveBackgroundResponse> {
  return fetchAPI<RemoveBackgroundResponse>(
    `/api/visual/imagenes/${encodeURIComponent(imageId)}/remove-background`,
    { method: 'POST' }
  )
}

export type AutoMaskMode = 'semantic' | 'foreground' | 'background'

export interface AutoMaskResponse {
  mask_data_url: string
}

/**
 * Generate an automatic segmentation mask for a generated image.
 * POST /api/visual/imagenes/{image_id}/auto-mask
 * Returns a PNG data URL with the detected mask.
 */
export async function generateAutoMask(
  imageId: string,
  mode: AutoMaskMode = 'foreground'
): Promise<AutoMaskResponse> {
  return fetchAPI<AutoMaskResponse>(
    `/api/visual/imagenes/${encodeURIComponent(imageId)}/auto-mask`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
    }
  )
}

export interface ReejecutarVisualResponse {
  mensaje: string
  tarea_id: string
  celery_task_id: string
}

/**
 * Re-execute a failed or cancelled visual task
 */
export async function reejecutarVisualTask(taskId: string): Promise<ReejecutarVisualResponse> {
  return fetchAPI<ReejecutarVisualResponse>(
    `/api/visual/tareas/${taskId}/reejecutar`,
    {
      method: 'POST',
    }
  )
}

export interface VisualSummary {
  total_imagenes: number
}

/**
 * Get visual summary for Dashboard (total images per organization)
 */
export async function getVisualSummary(): Promise<VisualSummary> {
  return fetchAPI<VisualSummary>('/api/visual/resumen')
}

// ============================================
// Visual Collections Types
// ============================================

export interface VisualCollection {
  id: string
  organization_id: string
  name: string
  description: string | null
  cover_image_id: string | null
  created_at: string
  updated_at: string | null
}

export interface VisualCollectionItem {
  visual_task_id: string
  sort_order: number
  added_at: string
}

export interface VisualCollectionDetail extends VisualCollection {
  items: VisualCollectionItem[]
}

export interface VisualCollectionCreateRequest {
  name: string
  description?: string | null
  cover_image_id?: string | null
}

export interface VisualCollectionUpdateRequest {
  name?: string
  description?: string | null
  cover_image_id?: string | null
}

// ============================================
// Visual Collections API Methods
// ============================================

export async function listVisualCollections(): Promise<VisualCollection[]> {
  return fetchAPI<VisualCollection[]>('/api/visual/colecciones')
}

export async function createVisualCollection(
  data: VisualCollectionCreateRequest
): Promise<VisualCollection> {
  return fetchAPI<VisualCollection>('/api/visual/colecciones', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getVisualCollection(collectionId: string): Promise<VisualCollectionDetail> {
  return fetchAPI<VisualCollectionDetail>(`/api/visual/colecciones/${collectionId}`)
}

export async function updateVisualCollection(
  collectionId: string,
  data: VisualCollectionUpdateRequest
): Promise<VisualCollection> {
  return fetchAPI<VisualCollection>(`/api/visual/colecciones/${collectionId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteVisualCollection(collectionId: string): Promise<void> {
  await fetchAPI<unknown>(`/api/visual/colecciones/${collectionId}`, { method: 'DELETE' })
}

export async function addTaskToCollection(
  collectionId: string,
  visualTaskId: string,
  sortOrder: number = 0
): Promise<Record<string, unknown>> {
  return fetchAPI<Record<string, unknown>>(`/api/visual/colecciones/${collectionId}/items`, {
    method: 'POST',
    body: JSON.stringify({ visual_task_id: visualTaskId, sort_order: sortOrder }),
  })
}

export async function removeTaskFromCollection(
  collectionId: string,
  taskId: string
): Promise<void> {
  await fetchAPI<unknown>(
    `/api/visual/colecciones/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(taskId)}`,
    { method: 'DELETE' }
  )
}

// ============================================
// Visual Templates
// ============================================

export interface VisualTemplate {
  id: string
  name: string
  category: string
  channel: string | null
  preview_url: string | null
  is_active: boolean
}

export interface VisualTemplateDetail extends VisualTemplate {
  config?: Record<string, unknown>
}

export interface ListTemplatesFilters {
  categoria?: string
  canal?: string
  solo_activos?: boolean
}

/**
 * List compositor templates
 */
export async function listTemplates(
  filters?: ListTemplatesFilters
): Promise<VisualTemplate[]> {
  let url = '/api/visual/templates'
  const params = new URLSearchParams()
  if (filters?.categoria) params.set('categoria', filters.categoria)
  if (filters?.canal) params.set('canal', filters.canal)
  if (filters?.solo_activos !== undefined) params.set('solo_activos', String(filters.solo_activos))
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return fetchAPI<VisualTemplate[]>(url)
}

/**
 * Get a single template by id
 */
export async function getTemplate(templateId: string): Promise<VisualTemplateDetail> {
  return fetchAPI<VisualTemplateDetail>(`/api/visual/templates/${templateId}`)
}

export interface GenerateImagePromptParams {
  tema: string
  contexto?: string
  estilo?: string
  canal?: string
}

export interface GenerateImagePromptResponse {
  prompt: string
  negative_prompt?: string
  estilo: string
  aspect_ratio: string
  model_recommendation: string
}

/**
 * Generate an image prompt using AI
 */
export async function generateImagePrompt(
  params: GenerateImagePromptParams
): Promise<GenerateImagePromptResponse> {
  const search = new URLSearchParams()
  search.set('tema', params.tema)
  if (params.contexto) search.set('contexto', params.contexto)
  if (params.estilo) search.set('estilo', params.estilo)
  if (params.canal) search.set('canal', params.canal)
  return fetchAPI<GenerateImagePromptResponse>(
    `/api/visual/generar-prompt-imagen?${search.toString()}`,
    { method: 'POST' }
  )
}

export interface TemplateCreateRequest {
  id: string
  name: string
  category: string
  channel?: string | null
  config: Record<string, unknown>
}

export interface TemplateUpdateRequest {
  name?: string
  config?: Record<string, unknown>
  is_active?: boolean
}

/**
 * Create a new compositor template
 */
export async function createTemplate(
  data: TemplateCreateRequest
): Promise<VisualTemplate> {
  return fetchAPI<VisualTemplate>('/api/visual/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update an existing template
 */
export async function updateTemplate(
  templateId: string,
  data: TemplateUpdateRequest
): Promise<VisualTemplateDetail> {
  return fetchAPI<VisualTemplateDetail>(`/api/visual/templates/${templateId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

// ============================================
// React Flow Trace Types
// ============================================

export interface ReactFlowNodeData {
  label: string
  description?: string | null
  agent_role?: string | null
  phase?: string | null
  status: 'pending' | 'running' | 'success' | 'error'
  tokens_used: number
  duration_ms: number
  input_preview?: string | null
  output_preview?: string | null
  metadata?: Record<string, unknown>
  timestamp?: string | null
}

export interface ReactFlowNodeItem {
  id: string
  type: string
  position: { x: number; y: number }
  data: ReactFlowNodeData
  style?: Record<string, unknown>
  className?: string
  parentNode?: string
  extent?: 'parent'
}

export interface ReactFlowEdgeItem {
  id: string
  source: string
  target: string
  type?: string
  animated?: boolean
  label?: string
  style?: Record<string, unknown>
  data?: Record<string, unknown>
}

export interface ReactFlowTraceExport {
  nodes: ReactFlowNodeItem[]
  edges: ReactFlowEdgeItem[]
  viewport: { x: number; y: number; zoom: number }
  metadata: {
    execution_id: string
    project_name: string
    started_at: string
    completed_at?: string | null
    stats: {
      total_nodes: number
      total_edges: number
      total_tokens: number
      execution_time_ms: number
    }
  }
}

export async function getTaskReactFlow(taskId: string): Promise<ReactFlowTraceExport> {
  return fetchAPI<ReactFlowTraceExport>(`/api/content/tareas/${taskId}/react-flow`)
}

// ============================================
// Costs API
// ============================================

export interface CostoDiario {
  fecha: string
  costo: number
  tokens: number
}

/**
 * Get daily costs for charts (last N days).
 */
export async function getCostsDiario(
  dias: number = 30
): Promise<CostoDiario[]> {
  return fetchAPI<CostoDiario[]>(`/api/costs/diario?dias=${dias}`)
}

// ============================================
// Backoffice API
// ============================================

export interface BackofficeOrg {
  id: string
  name: string
  industry: string | null
  website: string | null
  plan: string
  plan_id: string
  plan_override: string | null
  subscription_status: string
  current_period_end: string
  stripe_customer_id: string | null
  monthly_budget_limit: number
  logo_url: string | null
  created_at: string
  updated_at: string
  user_count?: number
  members?: BackofficeUser[]
}

export interface BackofficeUser {
  id: string
  email: string | null
  full_name: string | null
  role: string | null
  organization_id: string | null
  is_super_admin: boolean
  created_at: string
}

export interface BackofficeStats {
  total_organizations: number
  total_users: number
  plan_distribution: Record<string, number>
}

export interface BackofficeCreateOrgPayload {
  name: string
  industry?: string
  website?: string
  plan?: string
  monthly_budget_limit?: number
}

export interface BackofficeUpdateOrgPayload {
  name?: string
  industry?: string
  website?: string
  plan?: string
  plan_override?: string | null
  monthly_budget_limit?: number
}

export interface BackofficeCreateUserPayload {
  email: string
  password: string
  full_name: string
  organization_id?: string
  role?: string
  is_super_admin?: boolean
}

export interface BackofficeUpdateUserPayload {
  full_name?: string
  role?: string
  organization_id?: string | null
  is_super_admin?: boolean
}

export const backofficeApi = {
  getStats: (): Promise<BackofficeStats> =>
    fetchAPI<BackofficeStats>('/api/backoffice/stats'),

  listOrgs: (): Promise<BackofficeOrg[]> =>
    fetchAPI<BackofficeOrg[]>('/api/backoffice/organizations'),

  getOrg: (id: string): Promise<BackofficeOrg> =>
    fetchAPI<BackofficeOrg>(`/api/backoffice/organizations/${id}`),

  createOrg: (payload: BackofficeCreateOrgPayload): Promise<BackofficeOrg> =>
    fetchAPI<BackofficeOrg>('/api/backoffice/organizations', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateOrg: (id: string, payload: BackofficeUpdateOrgPayload): Promise<BackofficeOrg> =>
    fetchAPI<BackofficeOrg>(`/api/backoffice/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteOrg: (id: string): Promise<void> =>
    fetchAPI<void>(`/api/backoffice/organizations/${id}`, { method: 'DELETE' }),

  listUsers: (): Promise<BackofficeUser[]> =>
    fetchAPI<BackofficeUser[]>('/api/backoffice/users'),

  createUser: (payload: BackofficeCreateUserPayload): Promise<BackofficeUser> =>
    fetchAPI<BackofficeUser>('/api/backoffice/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateUser: (id: string, payload: BackofficeUpdateUserPayload): Promise<BackofficeUser> =>
    fetchAPI<BackofficeUser>(`/api/backoffice/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  resetPassword: (id: string): Promise<{ email: string; reset_link: string }> =>
    fetchAPI<{ email: string; reset_link: string }>(
      `/api/backoffice/users/${id}/reset-password`,
      { method: 'POST' }
    ),

  deleteUser: (id: string): Promise<void> =>
    fetchAPI<void>(`/api/backoffice/users/${id}`, { method: 'DELETE' }),
}

// ============================================
// Image Export API
// ============================================

export type ExportFormat = 'png' | 'jpg' | 'webp'

export interface ExportImageOptions {
  format?: ExportFormat
  quality?: number
  scale?: 1 | 2 | 3
}

/**
 * Export a generated image with format conversion, quality and scale options.
 * GET /api/visual/imagenes/{image_id}/export
 * Returns the binary blob of the converted image.
 */
export async function exportImage(
  imageId: string,
  options: ExportImageOptions = {}
): Promise<Blob> {
  const token = await (async () => {
    const { supabase } = await import('./supabase')
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token || null
  })()

  const params = new URLSearchParams()
  if (options.format) params.set('format', options.format)
  if (options.quality != null) params.set('quality', String(options.quality))
  if (options.scale != null) params.set('scale', String(options.scale))

  const url = `${API_BASE_URL}/api/visual/imagenes/${encodeURIComponent(imageId)}/export?${params.toString()}`

  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, { headers })
  if (!response.ok) {
    throw new ApiError(
      `Export failed: ${response.statusText}`,
      response.status,
      undefined,
      url,
      'GET'
    )
  }
  return response.blob()
}
